import { ComponentChildren } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import { onBeforeUnload } from '@components/router/before-unload';
import { showGlobal } from '@components/dialog/global-render';
import { VoiceRecorder } from '@components/voice-recorder';
import { useIntl } from 'shared/intl/use-intl';
import { WebcamCapture, WebcamMode } from './webcam-capture';
import { isPhoneLike, pickDeviceFiles, prefersNativeCapture } from './device-picker';

export { isPhoneLike, pickDeviceFiles, prefersNativeCapture };
export type { WebcamMode } from './webcam-capture';

type CaptureOpts = {
  signal?: AbortSignal;
  onClosed?(): void;
};

let isPanelOpen = false;

function CapturePanel({
  label,
  signal,
  onClose,
  onClosed,
  hasData,
  children,
}: {
  label: string;
  signal?: AbortSignal;
  onClose(): void;
  onClosed?(): void;
  hasData(): boolean;
  children: ComponentChildren;
}) {
  const intl = useIntl();
  useEffect(() => {
    const close = () => onClose();
    if (signal?.aborted) {
      close();
    }
    signal?.addEventListener('abort', close);
    const stopWarning = onBeforeUnload(() =>
      hasData() ? intl('Your recording will be lost.') : undefined,
    );
    return () => {
      stopWarning();
      signal?.removeEventListener('abort', close);
      isPanelOpen = false;
      onClosed?.();
    };
  }, []);

  return (
    <div
      class="fixed z-[70] bottom-4 right-4 left-4 sm:left-auto sm:w-96 overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-2xl animate-[an-drawer-up_0.2s_ease-out]"
      role="dialog"
      aria-label={label}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          onClose();
        }
      }}
    >
      {children}
    </div>
  );
}

export function isCapturePanelOpen() {
  return isPanelOpen;
}

function showPanel(render: (resolve: (file: File | undefined) => void) => JSX.Element) {
  isPanelOpen = true;
  return showGlobal<File>(({ resolve }) => render(resolve));
}

export function captureWithWebcam(mode: WebcamMode, opts: CaptureOpts = {}) {
  if (opts.signal?.aborted || isPanelOpen) {
    return Promise.resolve(undefined);
  }
  return showPanel((resolve) => <WebcamPanel mode={mode} opts={opts} resolve={resolve} />);
}

function WebcamPanel({
  mode,
  opts,
  resolve,
}: {
  mode: WebcamMode;
  opts: CaptureOpts;
  resolve(file: File | undefined): void;
}) {
  const intl = useIntl();
  const hasData = useRef(false);
  return (
    <CapturePanel
      label={mode === 'video' ? intl('Record video') : intl('Take photo')}
      signal={opts.signal}
      onClosed={opts.onClosed}
      hasData={() => hasData.current}
      onClose={() => resolve(undefined)}
    >
      <WebcamCapture
        mode={mode}
        onHasDataChange={(value) => {
          hasData.current = value;
        }}
        onDone={resolve}
        onCancel={() => resolve(undefined)}
      />
    </CapturePanel>
  );
}

function VoicePanel({
  opts,
  resolve,
}: {
  opts: CaptureOpts;
  resolve(file: File | undefined): void;
}) {
  const intl = useIntl();
  const hasData = useRef(false);
  return (
    <CapturePanel
      label={intl('Voice note')}
      signal={opts.signal}
      onClosed={opts.onClosed}
      hasData={() => hasData.current}
      onClose={() => resolve(undefined)}
    >
      <VoiceRecorder
        onRecordingChange={(value) => {
          hasData.current = value;
        }}
        onDone={resolve}
        onCancel={() => resolve(undefined)}
      />
    </CapturePanel>
  );
}

export function captureVoiceNote(opts: CaptureOpts = {}) {
  if (opts.signal?.aborted || isPanelOpen) {
    return Promise.resolve(undefined);
  }
  return showPanel((resolve) => <VoicePanel opts={opts} resolve={resolve} />);
}

export function captureMedia(
  mode: WebcamMode,
  onFiles: (files: File[]) => void,
  opts: CaptureOpts = {},
): 'native' | 'panel' | 'busy' {
  if (prefersNativeCapture()) {
    pickDeviceFiles(
      {
        accept: mode === 'video' ? 'video/*' : 'image/*',
        capture: mode === 'video' ? 'user' : 'environment',
      },
      onFiles,
    );
    return 'native';
  }
  if (isPanelOpen) {
    return 'busy';
  }
  void captureWithWebcam(mode, opts).then((file) => {
    if (file) {
      onFiles([file]);
    }
  });
  return 'panel';
}
